// D04 Mission 08 — QoS Classification and Marking (LAB): mark voice at the LAN edge, LLQ on the T1
import { reportBoard } from '../../ui/wanWidgets.js';
import { wan, wanRoutes, peakTraffic, wanLayout } from './topologies.js';
import { ios, flowQ, action } from './helpers.js';

function network() {
  const n = wan({ primary: true });
  wanRoutes(n, { backupRoutes: false });
  return peakTraffic(n, { business: false });
}
export const qosLines = (rt) => {
  const v = flowQ(rt, 'voice'); const b = flowQ(rt, 'backup'); const e = flowQ(rt, 'erp');
  return [
    `Batangas users: calls to HQ — ${v?.reachable ? `${v.quality} (loss ${v.loss} %, jitter ${v.jitter} ms)` : 'no audio / cannot connect'}`,
    ...(e ? [`Batangas users: ERP — ${e.reachable ? (e.quality === 'Good' ? 'responsive' : 'slow') : 'unreachable'}`] : []),
    `File server team: afternoon backup to HQ — ${b?.reachable ? (b.loss > 0 ? 'running, rate limited by the link' : 'running at full speed') : 'failing'}`,
  ];
};
const pol = (rt, ifc) => rt.net.devices.R2.interfaces[ifc]?.servicePolicy || {};
const markOk = (rt) => !!pol(rt, 'GigabitEthernet0/1').input && flowQ(rt, 'voice')?.dscp === 46;
const llqOk = (rt) => {
  const name = pol(rt, 'Serial0/0/0').output;
  const pm = rt.net.devices.R2.qos?.policyMaps?.[name];
  return !!pm && pm.classes.some((c) => c.priority) && flowQ(rt, 'voice')?.quality === 'Good';
};

export default {
  id: 'u08', num: '08', kind: 'LAB', title: 'QoS Classification and Marking', type: 'Configuration Lab', implemented: true,
  objective: 'Use Cisco MQC on the Batangas hub: classify voice at the LAN edge and mark it EF, then give EF a low-latency priority queue on the congested T1 — and verify both with show policy-map interface.',
  network, layout: wanLayout(network(), { title: 'Batangas peak hour: voice + backup over a 1.544 Mbps T1', qos: { from: 'R2', to: 'R1', label: 'QoS output → T1' } }), linkLights: true,
  stages: [
    {
      id: 'observe', phase: 'INVESTIGATE', title: 'See the congestion', devices: ['R2', 'PH-BTG'],
      content: `<p>Start with evidence. On R2: <code>show interfaces serial 0/0/0</code> — compare the <b>output rate</b> with the <b>BW</b>, and look at <b>txload</b>, <b>Total output drops</b> and the <b>Queueing strategy</b>. Then refresh the user reports.</p>`,
      widgets: [reportBoard({ id: 'd4u08-status', lines: qosLines, required: true })],
      evidence: [
        { id: 'int', text: 'R2: T1 load and drops examined', match: ios('R2', 'show interfaces <IF>', /Serial0\/0\/0 is up[\s\S]*txload 255\/255/) },
        { id: 'rep', text: 'User reports checked', match: action('status-refresh') },
      ],
      tasks: [{ id: 'seen', text: 'Congestion confirmed on the T1 (offered load above line rate)', check: () => true }],
      question: 'Which number in show interfaces tells you the T1 is saturated, and which tells you packets are being lost?',
    },
    {
      id: 'mark', phase: 'BUILD', title: 'Classify and mark at the LAN edge', devices: ['R2', 'PH-BTG'],
      content: `<p>The IP phones send voice unmarked (DSCP 0). Classify it where it <b>enters</b> the network and mark it <b>EF</b>, so every router after this one can recognise it with a simple DSCP match.</p>
<pre>R2(config)# class-map match-all VOICE-IN
R2(config-cmap)# match protocol rtp
R2(config-cmap)# exit
R2(config)# policy-map MARK-IN
R2(config-pmap)# class VOICE-IN
R2(config-pmap-c)# set dscp ef
R2(config-pmap-c)# exit
R2(config-pmap)# exit
R2(config)# interface gigabitEthernet 0/1
R2(config-if)# service-policy input MARK-IN</pre>
<p>Check it with <code>show policy-map interface gigabitEthernet 0/1</code>: the VOICE-IN class should be counting and marking packets.</p>`,
      evidence: [{ id: 'pm', text: 'R2: marking counters on G0/1', match: ios('R2', 'show policy-map interface <IF>', /Service-policy input[\s\S]*Packets marked [1-9]/) }],
      tasks: [{ id: 'mark', text: 'Voice leaves R2 marked EF (DSCP 46) by an input policy on G0/1', check: markOk, fail: 'Voice is not being marked EF on G0/1 input.' }],
      hints: ['<code>match protocol rtp</code> recognises voice media. The policy goes on the LAN interface in the <b>input</b> direction.'],
    },
    {
      id: 'queue', phase: 'BUILD → VERIFY', title: 'Priority queue on the T1', devices: ['R2', 'PH-BTG'],
      content: `<p>Now act on the marking where congestion happens — the T1, <b>output</b> direction:</p>
<pre>R2(config)# class-map match-all VOICE
R2(config-cmap)# match dscp ef
R2(config)# policy-map WAN-EDGE
R2(config-pmap)# class VOICE
R2(config-pmap-c)# priority 256
R2(config-pmap)# class class-default
R2(config-pmap-c)# fair-queue
R2(config)# interface serial 0/0/0
R2(config-if)# service-policy output WAN-EDGE</pre>
<p><code>priority 256</code> creates a low-latency queue of up to 256 kbps for voice (two G.711 calls need ~174 kbps). Everything else shares the rest fairly. Verify with <code>show policy-map interface serial 0/0/0</code> and the user reports.</p>`,
      widgets: [reportBoard({ id: 'd4u08-status2', lines: qosLines })],
      evidence: [{ id: 'pm', text: 'R2: VOICE class with 0 drops on the T1', match: ios('R2', 'show policy-map interface <IF>', /Service-policy output: WAN-EDGE[\s\S]*Class-map: VOICE[\s\S]*drop rate 0 bps/) }],
      tasks: [{ id: 'llq', text: 'LLQ policy on S0/0/0 output and calls are Good again', check: llqOk, fail: 'Voice is still suffering on the T1 — check the class match and where the policy is attached.' }],
      question: 'The backup still drops packets. Is that a problem? Why or why not?',
    },
  ],
  review: () => '<p>Classify and mark once, at the edge; queue wherever congestion can occur. The T1 is still full — QoS just made sure the calls are the last thing to suffer, not the first.</p>',
};
